// Every user-facing string of feature 002 (spec FR-004 to FR-046; research R11). Quoted spec copy is
// verbatim; labels the spec names in bold (Log in, Sign up, Resend email, …) are used as written.
// Labels the spec leaves open are kept short and plain. No string contains a typed value except
// where the spec's copy names it (<email>, <name>, <school name>).

export const AUTH_COPY = {
  product: "Akaiva Edu Power",

  actions: {
    logIn: "Log in",
    signUp: "Sign up",
    logOut: "Log out",
    forgotPassword: "Forgot password?",
    resendEmail: "Resend email",
    changeEmail: "Change email",
    sendNewLink: "Send a new link",
    tryAgain: "Try again",
    continue: "Continue",
    cancel: "Cancel",
    setPassword: "Set password",
    changePassword: "Change password",
    sendLink: "Send link",
    backToLogIn: "Back to Log in",
    showPassword: "Show password",
    hidePassword: "Hide password",
    resendIn: (seconds: number) => `Resend in ${seconds} s`,
  },

  fields: {
    name: "Name",
    email: "Email",
    password: "Password",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    mobile: "Mobile number (contact)",
    // FR-004: the mobile is a contact number for the school and the delivery partner, not verified.
    mobileHint:
      "Used by the school and the delivery partner to reach you. We don't verify it.",
    // FR-005: the rule is shown before the user types.
    passwordRule: "8 to 128 characters.",
  },

  /** Example text inside the empty fields of the shared sign-in frame. */
  placeholders: {
    email: "Enter your email address",
    password: "Enter your password",
  },

  signUp: {
    title: "Sign up",
    intro: {
      title: "Create your account",
      text: "Order your school's booklist in a few steps",
    },
    // FR-009 (hidden until both addresses are configured).
    termsLead: "By creating an account you agree to the ",
    terms: "Terms of use",
    and: " and the ",
    privacy: "Privacy policy",
  },

  checkInbox: {
    title: "Check your inbox",
    sentTo: (email: string) =>
      `We've sent a link to ${email}. Open it to confirm your email.`,
    resent: "We've sent the email again",
  },

  verifyEmail: {
    title: "Confirm your email",
    verifying: "Confirming your email…",
    confirmed: "Your email is confirmed",
    linkInvalid: "This link no longer works",
    linkInvalidHint: "Enter your email to get a new confirmation link.",
    // Spec edge case "link opened twice": the email may already be confirmed.
    alreadyConfirmed: "If you've already confirmed your email, you can log in.",
  },

  logIn: {
    title: "Log in",
    subtitle: "Enter your credentials to continue",
    intro: {
      title: "Welcome back",
      text: "Log in to continue to your account",
    },
    /** Phones: the heading names the app, as on the delivery agent app's log in. */
    phoneIntro: {
      title: "Parents",
      text: "Books and stationery from your school's booklist",
    },
    sessionEnded: "Your session has ended. Please log in again.",
  },

  forgot: {
    title: "Forgot password",
    intro: {
      title: "Forgot your password?",
      text: "We'll email you a link to set a new one",
    },
    sent: (email: string) =>
      `If an account exists for ${email}, we've sent a link to reset your password. The link works for 1 hour.`,
  },

  reset: {
    title: "Choose a new password",
    linkInvalid: "This link no longer works",
    doneApp: "Your password has been changed. Log in with your new password.",
    doneDriver:
      "Your password is set. Sign in in the Akaiva Edu Power Driver app.",
    doneAdmin:
      "Your password is set. Sign in to the Akaiva admin console at admin.akaiva-edu.com.",
  },

  invite: {
    title:
      "Set your password to finish setting up your Akaiva Edu Power account",
    doneApp: "Your password is set. Log in to continue.",
    doneDriver:
      "Your account is ready. Sign in in the Akaiva Edu Power Driver app.",
    doneAdmin:
      "Your password is set. Sign in to the Akaiva admin console at admin.akaiva-edu.com.",
    linkInvalid:
      "This invite link has expired or was already used. Ask the person who invited you to send a new one.",
  },

  interstitial: {
    text: (name: string) =>
      `You're signed in as ${name}. Continuing will sign you out on this device.`,
  },

  roles: {
    chooseTitle: "How do you want to continue?",
    continueAsParent: "Continue as parent",
    continueAsSchoolAdmin: (school: string | null) =>
      school === null
        ? "Continue as school admin"
        : `Continue as school admin · ${school}`,
    switchToSchoolAdmin: "Switch to school admin",
    switchToParent: "Switch to parent",
    refreshAccess: "Refresh access",
    accessUpdated: "Access updated",
    accessUpToDate: "Your access is up to date",
    schoolAdminRemoved: "Your school admin access has been removed.",
    parentRemoved: "Your parent access has been removed.",
    schoolAdminLabel: "School admin",
    schoolAdminOnWeb:
      "The school admin console is available on the web at app.akaiva-edu.com",
    noAccessAnyMore: "You don't have access to this any more",
  },

  refusals: {
    admin:
      "This account is for the Akaiva admin console at admin.akaiva-edu.com.",
    driver: "This account is for the Akaiva Edu Power Driver app.",
    noAccess:
      "This account doesn't have access to the Akaiva Edu Power app. Contact Akaiva if you think this is wrong.",
  },

  errors: {
    invalidCredentials: "Email or password is incorrect",
    notVerified: "Confirm your email first",
    sessionEnded: "Your session has ended. Please log in again.",
    rateLimitedIn: (wait: string) => `Too many attempts. Try again in ${wait}.`,
    rateLimited: "Too many attempts. Try again later.",
    connection: "No connection. Check your internet and try again.",
    generic: "Something went wrong. Please try again.",
    reference: (requestId: string) => `Reference: ${requestId}`,
  },

  fieldErrors: {
    required: {
      name: "Enter your name",
      email: "Enter your email",
      password: "Enter your password",
      confirmPassword: "Enter your password again",
      mobile: "Enter your mobile number",
    },
    invalidFormat: {
      name: "Use letters, spaces and punctuation only",
      email: "Enter a valid email address",
      password: "Check this field",
      confirmPassword: "Check this field",
      mobile: "Enter a valid 10-digit Indian mobile number",
    },
    tooShort: "Use at least 8 characters",
    tooLong: "Use at most 128 characters",
    nameTooLong: "Use at most 100 characters",
    emailTooLong: "Use at most 254 characters",
    tooCommon: "This password is too common. Choose another one.",
    matchesEmail: "Your password can't be your email address.",
    mismatch: "The passwords don't match",
    unknown: "Check this field",
  },
} as const;
